import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { pluck, shareReplay } from 'rxjs/operators';
import { Inject, InjectionToken } from '@angular/core';

import { GasPaginator } from '../gas-paginator/gas-paginator.namespace';
import { GasTable } from './gas-table.namespace';

import FilterValue = GasTable.FilterValue;
import SortingValue = GasTable.SortingValue;
import TableValue = GasTable.TableValue;

/** @description - тип предназначен для хранения pipe обработки данных для репозитория таблицы */
export type GasArgumentStream = (stream: Observable<TableValue[]>) => Observable<TableValue[]>;

/** @description - хранит названия get параметров необходимых для составления url запроса в репозитории таблицы */
export type GasUrlQueryParams = {
  page: string;
  rowsPerPage: string;
  filter: string;
  sorting: string;
};

/** @description - параметры настроек репозитория таблицы передаваемых через InjectionToken */
export type GasTableRepositoryParams = {
  defaultPaginatorData: GasPaginator.Data;
  queryUrl: string;
  queryParamNames: GasUrlQueryParams;
  dataPipe: GasArgumentStream;
};

/** @description - стандартные настройки пагинации */
export const DEFAULT_PAGINATOR_DATA: GasPaginator.Data = {
  perPage: 10,
  perPageList: [5, 10, 15, 25],
  total: 0,
  page: 1,
};

/** @description - стандартные названия get параметров */
export const DEFAULT_QUERY_PARAMS: GasUrlQueryParams = {
  page: 'page',
  rowsPerPage: 'rowsPerPage',
  filter: 'filter',
  sorting: 'sorting',
};

/** @description - стандартный pipe для полученных репозиторием данных */
export const DEFAULT_TABLE_DATA_PIPE = function(stream: Observable<TableValue[]>): Observable<TableValue[]> {
  return stream.pipe(pluck('data'));
};

const DEFAULT_REPOSITORY_PARAMS: GasTableRepositoryParams = {
  defaultPaginatorData: DEFAULT_PAGINATOR_DATA,
  queryUrl: '',
  queryParamNames: DEFAULT_QUERY_PARAMS,
  dataPipe: DEFAULT_TABLE_DATA_PIPE,
};

/** @description - injection token со стандартными настройками репозитория таблицы */
export const DEFAULT_REPOSITORY_PARAMS_TOKEN = new InjectionToken<GasTableRepositoryParams>('DEFAULT_REPOSITORY_PARAMS_TOKEN',
  {
    providedIn: 'root',
    factory(): GasTableRepositoryParams {
      return DEFAULT_REPOSITORY_PARAMS;
    },
  },
);

/** @description - класс, который используется по паттерну "репозиторий".
 * Необходимо создать ваш новый класс и расширить его базовым и переопределить методы и свойства, которые вам нужны.
 * */
export abstract class GasDefaultTableRepository {

  public sorting$: Observable<SortingValue>;

  public paginationData$: Observable<GasPaginator.Data>;

  public filter$: Observable<GasTable.FilterValue>;

  public refreshSubject$ = new BehaviorSubject<void>(null);

  public selectedRowChanged$ = new Subject<TableValue[] | TableValue>();

  public get filter(): FilterValue {
    return this.filterSubject$.value;
  }

  public set filter(filter: FilterValue) {
    this.filterSubject$.next(filter);
  }

  public get defaultSorting(): SortingValue {
    return this.innerDefaultSorting;
  }

  public set defaultSorting(sorting: SortingValue) {
    this.innerDefaultSorting = sorting;
  }

  public get sorting(): SortingValue {
    return this.sortingSubject$.value || this.defaultSorting;
  }

  public set sorting(sorting: SortingValue) {
    if (!sorting) {
      return;
    }
    this.sortingSubject$.next(sorting);
    this.refreshSubject$.next();
  }

  public get paginationData(): GasPaginator.Data {
    return this.paginationDataSubject$.value;
  }

  public set paginationData(paginationData: GasPaginator.Data) {
    this.paginationDataSubject$.next({...this.paginationData, ...paginationData});
  }

  public get tableData(): Observable<TableValue[]> {
    return this.tableData$.asObservable();
  }

  private innerDefaultSorting: SortingValue;

  private sortingSubject$ = new BehaviorSubject<SortingValue>(null);

  private paginationDataSubject$ = new BehaviorSubject<GasPaginator.Data>(this.gasTableRepositoryParams.defaultPaginatorData);

  private filterSubject$ = new BehaviorSubject<FilterValue>(null);

  private tableData$ = new BehaviorSubject<TableValue[]>([]);

  constructor(
    private httpClient: HttpClient,
    @Inject(DEFAULT_REPOSITORY_PARAMS_TOKEN) private gasTableRepositoryParams: GasTableRepositoryParams,
  ) {
    this.sorting$ = this.sortingSubject$.asObservable()
      .pipe(
        shareReplay(1),
      );
    this.filter$ = this.filterSubject$.asObservable()
      .pipe(
        shareReplay(1),
      );
    this.paginationData$ = this.paginationDataSubject$.asObservable()
      .pipe(
        shareReplay(1),
      );

    this.gasTableRepositoryParams = {
      defaultPaginatorData: this.gasTableRepositoryParams.defaultPaginatorData
        ? this.gasTableRepositoryParams.defaultPaginatorData : DEFAULT_PAGINATOR_DATA,

      queryUrl: this.gasTableRepositoryParams.queryUrl
        ? this.gasTableRepositoryParams.queryUrl : '',

      queryParamNames: this.gasTableRepositoryParams.queryParamNames
        ? this.gasTableRepositoryParams.queryParamNames : DEFAULT_QUERY_PARAMS,

      dataPipe: this.gasTableRepositoryParams.dataPipe
        ? this.gasTableRepositoryParams.dataPipe : DEFAULT_TABLE_DATA_PIPE,
    };
  }

  /** @description - метод формирует url и get параметры по данным фильтра, пагинации и сортировки */
  public getGeneratedUrl(): string {
    const params = this.gasTableRepositoryParams;
    const httpParams = new HttpParams();

    httpParams.append(params.queryParamNames.page, this.paginationData.page.toString());
    httpParams.append(params.queryParamNames.rowsPerPage, this.paginationData.perPage.toString());

    if (this.filter) {
      httpParams.append(params.queryParamNames.filter, JSON.stringify(this.filter));
    }
    if (this.sorting) {
      httpParams.append(params.queryParamNames.sorting, JSON.stringify(this.sorting));
    }

    return params?.queryUrl + httpParams.toString();
  }

  /** @description - используется для определения pipe возвращаемых данных при запросе на сервер */
  public dataPipe(observable: Observable<TableValue[]>): Observable<TableValue[]> {
    return this.gasTableRepositoryParams.dataPipe(observable);
  }

  /** @description - по умолчанию отправляет GET запрос и данные будут возвращены из data
   * для изменения можно изменить пайп через dataPipe/InjectionToken или переопределить метод
   * */
  public getData(): Observable<TableValue[]> {
    return this.httpClient.get<TableValue[]>(this.getGeneratedUrl()).pipe(
      this.dataPipe.bind(this),
    );
  };

  /** @description - обновляет состояние таблицы в репозитории */
  public updateTableValue(data: TableValue[]) {
    this.tableData$.next(data);
  }
}
